/**
 * API客户端
 * 封装所有与后端API的通信
 * 响应格式: { success: true, data: T, meta?: ResponseMeta }
 */

import type {
  Game,
  GameSummary,
  GameDetailResponse,
  TagInfo,
  CategoryConfig,
  PlatformDetail,
  SiteConfig,
  SiteSettings,
  FeaturesConfig,
  ListGamesParams,
  SearchParams,
  SearchResponse,
  PaginationMeta,
  ImageProxyParams,
  ApiResponse
} from './types';

export interface ApiClientOptions {
  baseURL: string;
  timeout?: number;
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private baseURL: string;
  private timeout: number;
  private headers: Record<string, string>;

  constructor(options: ApiClientOptions) {
    this.baseURL = options.baseURL.replace(/\/$/, '');
    this.timeout = options.timeout || 10000;
    this.headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };
  }

  /**
   * 基础请求方法
   */
  private async request<T>(
    method: string,
    endpoint: string,
    options: {
      params?: Record<string, string | number | undefined>;
      body?: unknown;
      headers?: Record<string, string>;
    } = {}
  ): Promise<{ data: T; meta?: PaginationMeta }> {
    // 构建URL
    let url = `${this.baseURL}${endpoint}`;
    
    // 添加查询参数
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    // 构建请求配置
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    const fetchOptions: RequestInit = {
      method,
      headers: {
        ...this.headers,
        ...options.headers
      },
      signal: controller.signal
    };

    if (options.body && method !== 'GET') {
      fetchOptions.body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);

      // 处理非2xx响应
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = null;
        }
        throw new ApiError(
          errorData?.error?.message || `HTTP ${response.status}`,
          response.status,
          errorData
        );
      }

      // 解析响应数据 (ApiResponse格式: {success, data, meta})
      const apiResponse = await response.json() as ApiResponse<T>;
      
      if (!apiResponse.success) {
        throw new ApiError(
          apiResponse.error?.message || 'API Error',
          response.status,
          apiResponse.error
        );
      }

      return {
        data: apiResponse.data as T,
        meta: apiResponse.meta as PaginationMeta | undefined
      };
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof ApiError) {
        throw error;
      }
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timeout', 408);
        }
        throw new ApiError(error.message, 0);
      }
      
      throw new ApiError('Unknown error', 0);
    }
  }

  // ==================== 公开API ====================

  /**
   * 获取站点配置
   * 后端返回: { success: true, data: { config: {...} } }
   */
  async getConfig(): Promise<SiteConfig> {
    const { data } = await this.request<{ config: SiteConfig }>('GET', '/api/config');
    return data.config;
  }

  /**
   * 获取站点设置（公开）
   * 后端返回: { success: true, data: { site: {...}, features: {...} } }
   */
  async getSettings(): Promise<{ site: SiteSettings; features: FeaturesConfig }> {
    const { data } = await this.request<{ site: SiteSettings; features: FeaturesConfig }>('GET', '/api/settings');
    return data;
  }

  /**
   * 获取游戏列表
   */
  async listGames(params: ListGamesParams = {}): Promise<{
    games: GameSummary[];
    meta: PaginationMeta;
  }> {
    const { data, meta } = await this.request<GameSummary[]>('GET', '/api/games', {
      params: {
        page: params.page || 1,
        limit: params.limit || 20,
        tag: params.tag,
        category: params.category,
        search: params.search,
        platform: params.platform,
        sort: params.sort || 'newest'
      }
    });
    return {
      games: data,
      meta: meta || { page: 1, limit: 20, total: data.length, hasMore: false }
    };
  }

  /**
   * 获取游戏详情
   * 支持通过ID或Slug查询
   * 后端返回: { success: true, data: { game, related } }
   */
  async getGame(idOrSlug: string): Promise<GameDetailResponse> {
    const { data } = await this.request<{ game: Game; related: GameSummary[] }>('GET', `/api/games/${idOrSlug}`);
    return { game: data.game, related: data.related };
  }

  /**
   * 获取标签列表
   * 后端返回: { success: true, data: { tags: [...] } }
   */
  async listTags(): Promise<TagInfo[]> {
    const { data } = await this.request<{ tags: TagInfo[] }>('GET', '/api/tags');
    return data.tags;
  }

  /**
   * 获取平台列表
   * 后端返回: { success: true, data: { platforms: [...] } }
   */
  async listPlatforms(): Promise<PlatformDetail[]> {
    const { data } = await this.request<{ platforms: PlatformDetail[] }>('GET', '/api/platforms');
    return data.platforms;
  }

  /**
   * 获取分类列表
   * 后端返回: { success: true, data: { categories: [...] } }
   */
  async listCategories(): Promise<CategoryConfig[]> {
    const { data } = await this.request<{ categories: CategoryConfig[] }>('GET', '/api/categories');
    return data.categories;
  }

  /**
   * 搜索游戏
   * 后端实际返回: { success, data: { games: Game[], total, query }, meta }
   */
  async search(params: SearchParams): Promise<SearchResponse> {
    const { data } = await this.request<SearchResponse>('GET', '/api/search', {
      params: {
        q: params.q,
        type: params.type || 'all'
      }
    });
    return data;
  }

  /**
   * 记录浏览
   */
  async trackView(gameId: string): Promise<{ success: boolean }> {
    const { data } = await this.request<{ success: boolean }>('POST', '/api/track/view', {
      body: { gameId }
    });
    return data;
  }

  /**
   * 记录点击
   */
  async trackClick(gameId: string, platformId: string): Promise<{ success: boolean }> {
    const { data } = await this.request<{ success: boolean }>('POST', '/api/track/click', {
      body: { gameId, platformId }
    });
    return data;
  }

  // ==================== 图片代理 ====================

  /**
   * 构建图片代理URL
   */
  buildImageUrl(gameId: string, type: 'cover' | 'icon' | 'banner', params?: ImageProxyParams): string;
  buildImageUrl(gameId: string, type: 'screenshot', index: number, params?: ImageProxyParams): string;
  buildImageUrl(
    gameId: string,
    type: 'cover' | 'icon' | 'banner' | 'screenshot',
    indexOrParams?: number | ImageProxyParams,
    maybeParams?: ImageProxyParams
  ): string {
    let url = `${this.baseURL}/image/${gameId}/${type}`;
    let params: ImageProxyParams | undefined;

    if (typeof indexOrParams === 'number') {
      url += `/${indexOrParams}`;
      params = maybeParams;
    } else {
      params = indexOrParams;
    }

    if (params) {
      const searchParams = new URLSearchParams();
      if (params.w) searchParams.append('w', String(params.w));
      if (params.h) searchParams.append('h', String(params.h));
      if (params.format) searchParams.append('format', params.format);
      if (params.quality) searchParams.append('quality', String(params.quality));
      
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return url;
  }

  /**
   * 获取R2图片直接URL
   */
  buildR2ImageUrl(r2Key: string): string {
    return `${this.baseURL}/image/r2/${r2Key}`;
  }
}

// 默认导出单例实例（用于简单场景）
let defaultClient: ApiClient | null = null;

export function createApiClient(options: ApiClientOptions): ApiClient {
  return new ApiClient(options);
}

export function getDefaultClient(): ApiClient {
  if (!defaultClient) {
    // 从环境变量或全局配置读取
    const baseURL = typeof window !== 'undefined' 
      ? (window as any).__API_BASE_URL__ || 'https://back.002038.xyz'
      : 'https://back.002038.xyz';
    
    defaultClient = new ApiClient({ baseURL });
  }
  return defaultClient;
}
